/* eslint-disable complexity */
import {AnyCountry, Locale} from '@aboutyou/config';
// eslint-disable-next-line no-restricted-imports

type NumericalDelimiter = ',' | '.';

export enum CurrencyCode {
  BGN = 'BGN',
  CHF = 'CHF',
  CZK = 'CZK',
  DKK = 'DKK',
  EUR = 'EUR',
  HRK = 'HRK',
  HUF = 'HUF',
  ILS = 'ILS',
  NOK = 'NOK',
  PLN = 'PLN',
  RON = 'RON',
  SAR = 'SAR',
  SEK = 'SEK',
  USD = 'USD',
}

interface PriceFormat {
  decimalPoint: NumericalDelimiter;
  thousandsSeparator: NumericalDelimiter | ' ' | "'" | '';
  signPosition: 'before' | 'after';
}

export type Currency = PriceFormat & {
  iso: string;
  negativeSign: string;
  sign: Record<string, string>;
  truncate: boolean;
  useIsoSign: boolean;
  hideVatInfo?: boolean;
};

const NumberFormatByCountry: Record<
  AnyCountry,
  Omit<PriceFormat, 'signPosition'>
> = {
  DE: {thousandsSeparator: '.', decimalPoint: ','},
  AT: {thousandsSeparator: '.', decimalPoint: ','},
  CH: {thousandsSeparator: "'", decimalPoint: '.'},
  NL: {thousandsSeparator: '.', decimalPoint: ','},
  BE: {thousandsSeparator: '.', decimalPoint: ','},
  LU: {thousandsSeparator: '.', decimalPoint: ','},
  PL: {thousandsSeparator: ' ', decimalPoint: ','},
  CZ: {thousandsSeparator: ' ', decimalPoint: ','},
  HU: {thousandsSeparator: '.', decimalPoint: ','},
  RO: {thousandsSeparator: '.', decimalPoint: ','},
  SI: {thousandsSeparator: '.', decimalPoint: ','},
  LT: {thousandsSeparator: '', decimalPoint: ','},
  LV: {thousandsSeparator: '', decimalPoint: ','},
  EE: {thousandsSeparator: '', decimalPoint: ','},
  HR: {thousandsSeparator: ' ', decimalPoint: ','},
  BG: {thousandsSeparator: ' ', decimalPoint: ','},
  FR: {thousandsSeparator: '', decimalPoint: ','},
  SE: {thousandsSeparator: ' ', decimalPoint: ','},
  DK: {thousandsSeparator: '.', decimalPoint: ','},
  FI: {thousandsSeparator: ' ', decimalPoint: ','},
  IT: {thousandsSeparator: '.', decimalPoint: ','},
  ES: {thousandsSeparator: '.', decimalPoint: ','},
  PT: {thousandsSeparator: '.', decimalPoint: ','},
  GR: {thousandsSeparator: '.', decimalPoint: ','},
  ZZ: {thousandsSeparator: '.', decimalPoint: ','},
  CY: {thousandsSeparator: '.', decimalPoint: ','},
  SK: {thousandsSeparator: '.', decimalPoint: ','},
};

export const getCurrencyConfigByLocale_UNSAFE = (locale: Locale): Currency => {
  switch (locale) {
    case Locale.de_DE:
      return {
        iso: CurrencyCode.EUR,
        sign: {[CurrencyCode.EUR]: '€'},
        negativeSign: '-',
        truncate: false,
        useIsoSign: false,
        signPosition: 'after',
        ...NumberFormatByCountry.DE,
      };
    case Locale.en_DE:
      return {
        iso: CurrencyCode.EUR,
        sign: {[CurrencyCode.EUR]: '€'},
        negativeSign: '-',
        truncate: false,
        useIsoSign: false,
        signPosition: 'before',
        thousandsSeparator: ',',
        decimalPoint: '.',
      };
    case Locale.de_AT:
    case Locale.en_AT:
      return {
        iso: CurrencyCode.EUR,
        sign: {[CurrencyCode.EUR]: '€'},
        negativeSign: '-',
        truncate: false,
        useIsoSign: false,
        signPosition: 'before',
        ...NumberFormatByCountry.AT,
      };
    case Locale.de_CH:
    case Locale.en_CH:
    case Locale.fr_CH:
    case Locale.it_CH:
      return {
        iso: CurrencyCode.CHF,
        sign: {},
        negativeSign: '-',
        truncate: false,
        useIsoSign: true,
        signPosition: 'before',
        ...NumberFormatByCountry.CH,
      };
    case Locale.nl_NL:
    case Locale.en_NL:
      return {
        iso: CurrencyCode.EUR,
        sign: {[CurrencyCode.EUR]: '€'},
        negativeSign: '-',
        truncate: false,
        useIsoSign: false,
        signPosition: 'before',
        ...NumberFormatByCountry.NL,
      };
    case Locale.nl_BE:
      return {
        iso: CurrencyCode.EUR,
        sign: {[CurrencyCode.EUR]: '€'},
        negativeSign: '-',
        truncate: false,
        useIsoSign: false,
        signPosition: 'before',
        ...NumberFormatByCountry.BE,
      };
    case Locale.fr_BE:
      return {
        iso: CurrencyCode.EUR,
        sign: {[CurrencyCode.EUR]: '€'},
        negativeSign: '-',
        truncate: false,
        useIsoSign: false,
        signPosition: 'after',
        ...NumberFormatByCountry.BE,
      };
    case Locale.fr_LU:
      return {
        iso: CurrencyCode.EUR,
        sign: {[CurrencyCode.EUR]: '€'},
        negativeSign: '-',
        truncate: false,
        useIsoSign: false,
        signPosition: 'after',
        ...NumberFormatByCountry.LU,
      };
    case Locale.pl_PL:
      return {
        iso: CurrencyCode.PLN,
        sign: {[CurrencyCode.PLN]: 'zł'},
        negativeSign: '-',
        truncate: false,
        useIsoSign: false,
        signPosition: 'after',
        ...NumberFormatByCountry.PL,
      };
    case Locale.cs_CZ:
      return {
        iso: CurrencyCode.CZK,
        sign: {[CurrencyCode.CZK]: 'Kč'},
        negativeSign: '-',
        truncate: true,
        useIsoSign: false,
        signPosition: 'after',
        ...NumberFormatByCountry.CZ,
      };
    case Locale.sk_SK:
      return {
        iso: CurrencyCode.EUR,
        sign: {[CurrencyCode.EUR]: '€'},
        negativeSign: '-',
        truncate: false,
        useIsoSign: false,
        signPosition: 'after',
        ...NumberFormatByCountry.SK,
      };
    case Locale.hu_HU:
      return {
        iso: CurrencyCode.HUF,
        sign: {[CurrencyCode.HUF]: 'Ft'},
        negativeSign: '-',
        truncate: true,
        useIsoSign: false,
        signPosition: 'after',
        ...NumberFormatByCountry.HU,
      };
    case Locale.ro_RO:
      return {
        iso: CurrencyCode.RON,
        sign: {[CurrencyCode.RON]: 'lei'},
        negativeSign: '-',
        truncate: false,
        useIsoSign: false,
        signPosition: 'after',
        ...NumberFormatByCountry.RO,
      };
    case Locale.sl_SI:
      return {
        iso: CurrencyCode.EUR,
        sign: {[CurrencyCode.EUR]: '€'},
        negativeSign: '-',
        truncate: false,
        useIsoSign: false,
        signPosition: 'after',
        ...NumberFormatByCountry.SI,
      };
    case Locale.lt_LT:
      return {
        iso: CurrencyCode.EUR,
        sign: {[CurrencyCode.EUR]: '€'},
        negativeSign: '-',
        truncate: false,
        useIsoSign: false,
        signPosition: 'after',
        ...NumberFormatByCountry.LT,
      };
    case Locale.lv_LV:
    case Locale.ru_LV:
      return {
        iso: CurrencyCode.EUR,
        sign: {[CurrencyCode.EUR]: '€'},
        negativeSign: '-',
        truncate: false,
        useIsoSign: false,
        signPosition: 'after',
        ...NumberFormatByCountry.LV,
      };
    case Locale.et_EE:
    case Locale.ru_EE: {
      return {
        iso: CurrencyCode.EUR,
        sign: {[CurrencyCode.EUR]: '€'},
        negativeSign: '-',
        truncate: false,
        useIsoSign: false,
        signPosition: 'after',
        ...NumberFormatByCountry.EE,
      };
    }
    case Locale.hr_HR: {
      return {
        iso: CurrencyCode.EUR,
        sign: {[CurrencyCode.EUR]: '€', [CurrencyCode.HRK]: 'kn'},
        negativeSign: '-',
        truncate: false,
        useIsoSign: false,
        signPosition: 'after',
        ...NumberFormatByCountry.HR,
      };
    }
    case Locale.bg_BG:
      return {
        iso: CurrencyCode.EUR,
        sign: {[CurrencyCode.BGN]: 'лв.', [CurrencyCode.EUR]: '€'},
        negativeSign: '-',
        truncate: false,
        useIsoSign: false,
        signPosition: 'after',
        ...NumberFormatByCountry.BG,
      };
    case Locale.fr_FR:
      return {
        iso: CurrencyCode.EUR,
        sign: {[CurrencyCode.EUR]: '€'},
        negativeSign: '-',
        truncate: false,
        useIsoSign: false,
        signPosition: 'after',
        ...NumberFormatByCountry.FR,
      };
    case Locale.sv_SE:
      return {
        iso: CurrencyCode.SEK,
        sign: {[CurrencyCode.SEK]: 'kr'},
        negativeSign: '-',
        truncate: false,
        useIsoSign: false,
        signPosition: 'after',
        ...NumberFormatByCountry.SE,
      };
    case Locale.da_DK:
      return {
        iso: CurrencyCode.DKK,
        sign: {[CurrencyCode.DKK]: 'kr'},
        negativeSign: '-',
        truncate: false,
        useIsoSign: false,
        signPosition: 'after',
        ...NumberFormatByCountry.DK,
      };
    case Locale.fi_FI:
      return {
        iso: CurrencyCode.EUR,
        sign: {[CurrencyCode.EUR]: '€'},
        negativeSign: '-',
        truncate: false,
        useIsoSign: false,
        signPosition: 'after',
        ...NumberFormatByCountry.FI,
      };
    case Locale.it_IT:
      return {
        iso: CurrencyCode.EUR,
        sign: {[CurrencyCode.EUR]: '€'},
        negativeSign: '-',
        truncate: false,
        useIsoSign: false,
        signPosition: 'after',
        ...NumberFormatByCountry.IT,
      };
    case Locale.es_ES:
      return {
        iso: CurrencyCode.EUR,
        sign: {[CurrencyCode.EUR]: '€'},
        negativeSign: '-',
        truncate: false,
        useIsoSign: false,
        signPosition: 'after',
        ...NumberFormatByCountry.ES,
      };
    case Locale.pt_PT:
      return {
        iso: CurrencyCode.EUR,
        sign: {[CurrencyCode.EUR]: '€'},
        negativeSign: '-',
        truncate: false,
        useIsoSign: false,
        signPosition: 'after',
        ...NumberFormatByCountry.PT,
      };
    case Locale.el_GR:
      return {
        iso: CurrencyCode.EUR,
        sign: {[CurrencyCode.EUR]: '€'},
        negativeSign: '-',
        truncate: false,
        useIsoSign: false,
        signPosition: 'after',
        ...NumberFormatByCountry.GR,
      };
    case Locale.en_ZZ:
    case Locale.es_ZZ:
      return {
        iso: CurrencyCode.USD,
        sign: {
          [CurrencyCode.USD]: '$',
          [CurrencyCode.SAR]: '$',
          [CurrencyCode.ILS]: '$',
        },
        negativeSign: '-',
        truncate: false,
        useIsoSign: false,
        hideVatInfo: true,
        signPosition: 'before',
        ...NumberFormatByCountry.ZZ,
      };
    case Locale.el_CY:
      return {
        iso: CurrencyCode.EUR,
        sign: {[CurrencyCode.EUR]: '€'},
        negativeSign: '-',
        truncate: false,
        useIsoSign: false,
        signPosition: 'after',
        ...NumberFormatByCountry.CY,
      };
  }
};
